/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit';
import isEqual from 'lodash/isEqual';
import get from 'lodash/get';
import { visitCanvases, layoutVersion, validateLayoutGraph, structuralShape, conversionError } from '../core/layout/layoutContract';
import CanvasResolver from '../core/renderer/CanvasResolver';
import { columnCount, resizeColumns } from '../core/layout/columns';
import * as c from '../../../constants/actionTypes/settings';

const sliceName = 'searchExperience';
const HISTORY_LIMIT = 50;
const copyConfig = (config) => JSON.parse(JSON.stringify(config));
const clearHistory = (state) => { state.editorHistory = { past: [], future: [] }; };
const clearValidation = (state) => {
  state.validationErrors = [];
  state.isValid = true;
  state.error = null;
};

// One history surrounds every draft mutation, including nested property edits.
// The persisted config stays independent of this local editor-only state.
const withHistory = (apply) => (state, action) => {
  const before = copyConfig(state.config);
  const wasDirty = state.isDirty;
  apply(state, action);
  let layoutError = null;
  visitCanvases(state.config?.search?.views, (canvas, path) => {
    if (layoutError) return;
    const previous = get(before, path);
    const changed = !isEqual(structuralShape(previous), structuralShape(canvas));
    if (changed && state.schema?.layoutContracts?.createV2 && layoutVersion(canvas) === 1) {
      layoutError = previous ? conversionError(previous) : null;
      if (!layoutError) canvas.layoutContractVersion = 2;
    }
    if (layoutVersion(canvas) === 2 && !isEqual(previous, canvas)) {
      const error = validateLayoutGraph(canvas);
      if (error !== true) layoutError = `${path}: ${error}`;
    }
  });
  if (layoutError) {
    state.config = before;
    state.error = layoutError;
    state.isDirty = wasDirty;
    return;
  }
  if (isEqual(before, state.config)) {
    state.isDirty = wasDirty;
    return;
  }
  const history = state.editorHistory || { past: [], future: [] };
  state.editorHistory = {
    past: [...history.past, before].slice(-HISTORY_LIMIT),
    future: [],
  };
  state.isDirty = !isEqual(state.config, state.originalConfig);
  clearValidation(state);
};

// Return the owner of the same canvas that the editor displays. Disabled device
// overrides intentionally inherit the base, matching CanvasResolver.resolve.
const resolveCanvasOwner = (config, { view, screen, device, visualSearch }) => {
  const v2Owner = CanvasResolver.resolveV2Owner(config, { view, screen, device, visualSearch });
  if (v2Owner !== undefined) return v2Owner;
  const viewConfig = config?.search?.views?.[view];
  const base = viewConfig?.screens?.[screen];
  if (!base?.canvas) return null;
  const override = viewConfig.device?.[device]?.[screen];
  return override?.canvas && !CanvasResolver.isDisabledCanvas(override.canvas) ? override : base;
};

// Deep merge utility (same as SettingsPreviewSlice)
const mergeDeep = (target, patch) => {
  Object.entries(patch || {}).forEach(([k, v]) => {
    const tv = target?.[k];
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      if (!tv || typeof tv !== 'object' || Array.isArray(tv)) target[k] = {};
      mergeDeep(target[k], v);
    } else {
      target[k] = v;
    }
  });
};

const initialState = {
  // Loading states
  isLoading: false,
  isLoadingRegistry: false,
  isSaving: false,
  error: null,
  conflictConfig: null,

  // Config data
  config: null, // Current working config
  originalConfig: null, // Original config for diff comparison
  schema: null, // Config schema description

  // Mode management
  mode: 'preview', // Editors always work on a draft; publishing copies it to production.

  // Validation
  validationErrors: [],
  isValid: true,

  // Dirty state tracking
  isDirty: false,
  lastSaved: null,
  editorHistory: { past: [], future: [] },

  // UI state
  activeTab: 'layout', // 'layout' | 'behavior' | 'theme'
  currentView: 'resultsPage', // 'searchBox' | 'resultsPage' | 'product'
  currentScreen: 'results', // 'results' | 'noResults' | 'empty'
  currentDevice: 'desktop', // 'desktop' | 'mobile'
};

const normalizeError = (value, fallback = 'Search experience request failed') => {
  if (typeof value === 'string' && value) return value;
  if (typeof value?.message === 'string' && value.message) return value.message;
  if (typeof value?.error === 'string' && value.error) return value.error;
  if (typeof value?.error?.message === 'string' && value.error.message) {
    return value.error.message;
  }
  return fallback;
};

const SearchExperienceSlice = createSlice({
  name: sliceName,
  initialState,
  reducers: {
    // Loading config
    loadConfigRequest(state) {
      state.isLoading = true;
      state.error = null;
    },
    loadConfigSuccess(state, { payload }) {
      state.isLoading = false;
      state.conflictConfig = null;
      state.config = payload.config;
      clearHistory(state);
      state.originalConfig = JSON.parse(JSON.stringify(payload.config)); // Deep clone
      state.mode = payload.mode;
      state.validationErrors = [];
      state.isValid = true;
      state.isDirty = false;
      state.error = null;

      // Auto-select first available view if current view doesn't exist or not set
      const availableViews = payload.config?.search?.views ? Object.keys(payload.config.search.views) : [];
      if (availableViews.length > 0 && !availableViews.includes(state.currentView)) {
        [state.currentView] = availableViews;
      }
    },
    loadConfigFailure(state, { payload }) {
      state.isLoading = false;
      state.error = normalizeError(payload?.error ?? payload);
    },

    // Loading schema
    loadSchemaSuccess(state, { payload }) {
      state.schema = payload.schema;
    },

    // Loading component registry
    loadRegistryRequest(state) {
      state.isLoadingRegistry = true;
      state.error = null;
    },
    loadRegistrySuccess(state) {
      state.isLoadingRegistry = false;
    },
    loadRegistryFailure(state, { payload }) {
      state.isLoadingRegistry = false;
      state.error = normalizeError(payload?.error ?? payload);
    },

    // Saving config
    saveConfigRequest(state) {
      state.isSaving = true;
      state.error = null;
    },
    saveConfigSuccess(state, { payload }) {
      state.isSaving = false;
      if (payload?.config) state.config = payload.config;
      state.error = null;
      state.validationErrors = [];
      state.isValid = true;
      state.originalConfig = JSON.parse(JSON.stringify(state.config)); // Update baseline
      clearHistory(state);
      state.isDirty = false;
      state.lastSaved = new Date().toISOString();
      state.error = null;
    },
    saveConfigFailure(state, { payload }) {
      state.isSaving = false;
      state.conflictConfig = payload?.currentConfig || null;
      state.error = normalizeError(payload?.error ?? payload);
    },

    // Config updates
    updateConfig: withHistory((state, { payload }) => {
      if (!state.config) {
        state.config = payload;
      } else {
        mergeDeep(state.config, payload);
      }
      state.isDirty = true;
    }),

    updateTheme: withHistory((state, { payload }) => {
      if (!state.config) return;
      if (!state.config.search) state.config.search = {};
      if (!state.config.search.theme) state.config.search.theme = {};
      mergeDeep(state.config.search.theme, payload);
      state.isDirty = true;
    }),

    updateBehavior: withHistory((state, { payload }) => {
      if (!state.config) return;
      if (!state.config.search) state.config.search = {};
      if (!state.config.search.behavior) state.config.search.behavior = {};
      mergeDeep(state.config.search.behavior, payload);
      state.isDirty = true;
    }),

    updateCanvas: withHistory((state, { payload }) => {
      if (!state.config) return;
      const owner = resolveCanvasOwner(state.config, payload);
      if (owner) {
        owner.canvas = copyConfig(payload.canvas);
      } else {
        // Preserve the existing action API for callers initializing a new screen.
        const { view, screen, canvas } = payload;
        if (!view || !screen) return;
        if (!state.config.search) state.config.search = {};
        if (!state.config.search.views) state.config.search.views = {};
        if (!state.config.search.views[view]) state.config.search.views[view] = {};
        const viewConfig = state.config.search.views[view];
        if (!viewConfig.screens) viewConfig.screens = {};
        if (!viewConfig.screens[screen]) viewConfig.screens[screen] = {};
        viewConfig.screens[screen].canvas = copyConfig(canvas);
      }
      state.isDirty = true;
    }),

    applyColumnLayout: withHistory((state, { payload }) => {
      const owner = resolveCanvasOwner(state.config, payload);
      if (!owner || !Array.isArray(payload.weights)) return;
      const index = owner.canvas.nodes?.findIndex(node => node.id === payload.nodeId);
      if (index === undefined || index < 0) return;
      const resized = resizeColumns(owner.canvas.nodes[index], payload.weights.length, payload.weights);
      if (resized) owner.canvas.nodes[index] = resized;
    }),

    updateNodeProps: withHistory((state, { payload }) => {
      const { nodeId, props } = payload;
      const owner = resolveCanvasOwner(state.config, payload);
      if (!owner) return;
      const { canvas } = owner;
      const nodeIndex = canvas.nodes?.findIndex((n) => n.id === nodeId);

      if (nodeIndex !== -1 && nodeIndex !== undefined) {
        const node = canvas.nodes[nodeIndex];
        if (node.type === 'columns') {
          const count = props.columns === undefined ? columnCount(node) : Number(props.columns);
          const resized = resizeColumns(node, count);
          if (!resized) return;
          canvas.nodes[nodeIndex] = {
            ...resized,
            props: { ...node.props, ...props, columns: count,
              ...(count !== columnCount(node) ? { template: resized.props.template, ratio: resized.props.ratio } : {}) },
          };
        } else {
          canvas.nodes[nodeIndex] = { ...node, props: { ...props } };
        }
        state.isDirty = true;
      }
    }),

    // Reset to original
    resetConfig(state) {
      clearHistory(state);
      if (state.originalConfig) {
        state.config = JSON.parse(JSON.stringify(state.originalConfig));
        state.isDirty = false;
        state.validationErrors = [];
        state.isValid = true;
        state.error = null;
      }
    },

    undoConfig(state) {
      if (state.isSaving || state.isLoading || !state.editorHistory?.past.length) return;
      const history = state.editorHistory;
      history.future = [...history.future, copyConfig(state.config)].slice(-HISTORY_LIMIT);
      state.config = history.past.pop();
      state.isDirty = !isEqual(state.config, state.originalConfig);
      clearValidation(state);
    },

    redoConfig(state) {
      if (state.isSaving || state.isLoading || !state.editorHistory?.future.length) return;
      const history = state.editorHistory;
      history.past = [...history.past, copyConfig(state.config)].slice(-HISTORY_LIMIT);
      state.config = history.future.pop();
      state.isDirty = !isEqual(state.config, state.originalConfig);
      clearValidation(state);
    },

    // Mode switching
    setMode(state, { payload }) {
      state.mode = payload.mode;
    },

    // Validation
    setValidationErrors(state, { payload }) {
      state.validationErrors = payload.errors || [];
      state.isValid = payload.errors.length === 0;
    },

    validateConfig(state) {
      // Client-side validation logic
      const errors = [];

      if (!state.config) {
        errors.push({ field: 'config', message: 'Config is required' });
      }

      if (state.config && !state.config.schemaVersion) {
        errors.push({ field: 'schemaVersion', message: 'Schema version is required' });
      }

      state.validationErrors = errors;
      state.isValid = errors.length === 0;
    },

    // UI state
    setActiveTab(state, { payload }) {
      state.activeTab = payload.tab;
    },

    setCurrentView(state, { payload }) {
      state.currentView = payload.view;
    },

    setCurrentScreen(state, { payload }) {
      state.currentScreen = payload.screen;
    },

    setCurrentDevice(state, { payload }) {
      state.currentDevice = payload.device;
    },

    // Clear error
    clearError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(c.LOAD_SEARCH_EXPERIENCE_CONFIG, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(c.LOAD_SEARCH_EXPERIENCE_CONFIG_SUCCESS, (state, { payload }) => {
        state.isLoading = false;
        state.config = payload.config;
        clearHistory(state);
        clearValidation(state);
        state.originalConfig = JSON.parse(JSON.stringify(payload.config));
        state.mode = payload.mode;
        state.isDirty = false;
        state.error = null;

        const availableViews = payload.config?.search?.views ? Object.keys(payload.config.search.views) : [];
        if (availableViews.length > 0 && !availableViews.includes(state.currentView)) {
          state.currentView = availableViews[0];
        }
      })
      .addCase(c.LOAD_SEARCH_EXPERIENCE_CONFIG_FAILED, (state, { payload }) => {
        state.isLoading = false;
        state.error = normalizeError(payload);
      });
  },
});

export const { actions, reducer } = SearchExperienceSlice;

export const {
  loadConfigRequest,
  loadConfigSuccess,
  loadConfigFailure,
  loadSchemaSuccess,
  loadRegistryRequest,
  loadRegistrySuccess,
  loadRegistryFailure,
  saveConfigRequest,
  saveConfigSuccess,
  saveConfigFailure,
  updateConfig,
  updateNodeProps,
  applyColumnLayout,
  setCurrentView,
  setCurrentScreen,
  setCurrentDevice,
  updateTheme,
  updateBehavior,
  updateCanvas,
  resetConfig,
  undoConfig,
  redoConfig,
  setMode,
  setValidationErrors,
  validateConfig,
  setActiveTab,
  clearError,
} = actions;

export default reducer;
